import React from 'react';
import dayjs from 'dayjs';
import type { EndofDaySummary, PostTradeErrorSummary } from './type';

// The day at a glance, shown above the detailed end-of-day sections: one score with a grade, a one-line verdict,
// the five steps with a shortcut to whatever is missing, the top mistakes, and yesterday's commitment.

type StepIndex = 0 | 1 | 2 | 3 | 4;

/** Saved "lesson & commitment" notes of a journal (eod_extra_data.summary_note.eod_review_data, with legacy fallbacks) */
export function parseEodNotes(journal: any): { brokenPlan: string; deviation: string; commitment: string } {
  const empty = { brokenPlan: '', deviation: '', commitment: '' };
  if (!journal) return empty;
  try {
    const extra = typeof journal.eod_extra_data === 'string' ? JSON.parse(journal.eod_extra_data) : journal.eod_extra_data;
    let review: any = null;
    if (extra?.summary_note) {
      const note = typeof extra.summary_note === 'string' ? JSON.parse(extra.summary_note) : extra.summary_note;
      review = note?.eod_review_data;
    }
    return {
      brokenPlan: review?.note_broken_plan || journal.note_broken_plan || extra?.worst_thing || '',
      deviation: review?.note_deviation || journal.note_deviation || '',
      commitment: review?.note_commitment || journal.note_commitment || extra?.best_thing || '',
    };
  } catch {
    return { brokenPlan: journal.note_broken_plan || '', deviation: journal.note_deviation || '', commitment: journal.note_commitment || '' };
  }
}

interface EodSummaryCardProps {
  eodSummary: EndofDaySummary | null;
  errorSummary: PostTradeErrorSummary | null;
  /** Weighted sum of the four journal stages (0-100) */
  stageTotal: number;
  /** Compliance score of the trades themselves (0-100) */
  complianceScore: number;
  reflectionDone: boolean;
  prevCommitment: { date: string; text: string } | null;
  onGoToStep?: (step: StepIndex) => void;
  onGoToReflection: () => void;
  onGoToOrders: () => void;
}

const STEP_NAMES = ['Đầu ngày', 'Trước lệnh', 'Trong lệnh', 'Sau lệnh'];

function gradeOf(score: number) {
  if (score >= 85) return { grade: 'A', tone: 'good', text: 'Rất tốt' };
  if (score >= 70) return { grade: 'B', tone: 'good', text: 'Tốt' };
  if (score >= 50) return { grade: 'C', tone: 'mid', text: 'Cần cố gắng' };
  return { grade: 'D', tone: 'bad', text: 'Chưa đạt' };
}

// Match a stage from the API to its journal step by title; fall back to its position
function stepOfStage(title: string, index: number): StepIndex {
  const t = (title || '').toLowerCase();
  if (t.includes('đầu ngày')) return 0;
  if (t.includes('trước lệnh')) return 1;
  if (t.includes('trong lệnh')) return 2;
  if (t.includes('sau lệnh')) return 3;
  return Math.min(index, 3) as StepIndex;
}

const EodSummaryCard: React.FC<EodSummaryCardProps> = ({
  eodSummary, errorSummary, stageTotal, complianceScore, reflectionDone, prevCommitment,
  onGoToStep, onGoToReflection, onGoToOrders,
}) => {
  const score = Math.round(stageTotal * 10) / 10;

  const stages = (eodSummary?.phases_breakdown || []).map((p, i) => {
    const pct = p.max_score > 0 ? Math.round((p.earned_score / p.max_score) * 100) : Math.round(p.percent || 0);
    // Auto-scored stages (in / after trade) with nothing to score: no trades that day, so they do not apply
    const na = !!p.is_auto_calculated && !p.total_mucs;
    return { step: stepOfStage(p.title, i), name: STEP_NAMES[stepOfStage(p.title, i)] || p.title, earned: p.earned_score, max: p.max_score, pct, na };
  });
  const applicable = stages.filter((s) => !s.na);
  const maxApplicable = applicable.reduce((sum, s) => sum + s.max, 0) || 100;
  // Grade on the stages that apply: a no-trade day can't earn the 40 trade points, which made a complete day a "D"
  const g = gradeOf((score / maxApplicable) * 100);

  // One-line verdict: best and weakest stage
  let verdict = 'Chưa có dữ liệu cho ngày này — hãy bắt đầu từ bước Đầu ngày.';
  if (applicable.some((s) => s.earned > 0)) {
    const sorted = [...applicable].sort((a, b) => b.pct - a.pct);
    const best = sorted[0];
    const weak = sorted[sorted.length - 1];
    verdict = weak.pct >= 80
      ? `Bạn hoàn thành tốt cả ${applicable.length} giai đoạn hôm nay. Giữ nhịp này!`
      : `Bạn làm tốt ${best.name} (${best.pct}%), nhưng ${weak.name} mới đạt ${weak.pct}% — ưu tiên bổ sung phần này.`;
  }

  const issues = [
    { key: 'no_sl', label: 'Không đặt Stop Loss', data: errorSummary?.no_sl },
    { key: 'moved_sl', label: 'Dời Stop Loss', data: errorSummary?.moved_sl },
    { key: 'closed_early', label: 'Chốt lời sớm / chốt non', data: errorSummary?.closed_early },
    { key: 'bad_setup', label: 'Vào lệnh sai setup', data: { count: (eodSummary?.setup_matrix?.bad_win?.count || 0) + (eodSummary?.setup_matrix?.bad_loss?.count || 0), trades: [] as string[] } },
  ]
    .filter((i) => (i.data?.count || 0) > 0)
    .sort((a, b) => (b.data?.count || 0) - (a.data?.count || 0))
    .slice(0, 3);

  const pnl = eodSummary?.compliance_score?.pnl;

  return (
    <div className="card eod-summary">
      {/* Same header bar as the numbered sections below */}
      <div className="card-head">
        <div>
          <h2><span className="num">1</span>Tóm tắt ngày</h2>
        </div>
      </div>
      <div className="eod-summary__top">
        <div className={`eod-summary__grade eod-summary__grade--${g.tone}`}>
          <span className="eod-summary__letter">{g.grade}</span>
          <span className="eod-summary__score">{score}<small>/{maxApplicable}</small></span>
        </div>
        <div className="eod-summary__head">
          <div className="eod-summary__title">Xếp hạng: <span>{g.text}</span></div>
          <p className="eod-summary__verdict">{verdict}{stages.some((s) => s.na) ? ' Hôm nay không có lệnh nên chỉ chấm các giai đoạn trước khi vào lệnh.' : ''}</p>
          <div className="eod-summary__meta">
            <span>Tuân thủ khi vào lệnh: <b>{stages.some((s) => s.na) && !complianceScore ? 'không có lệnh' : `${complianceScore}/100`}</b></span>
            {pnl && (
              <span>P&L ngày: <b className={pnl.is_profit ? 'is-profit' : 'is-loss'}>{pnl.formatted}</b> <em>(không tính vào điểm)</em></span>
            )}
          </div>
        </div>
      </div>

      <div className="eod-summary__steps">
        {stages.map((s) => (
          <div key={s.step} className="eod-summary__step">
            <div className="eod-summary__step-row">
              <span className="eod-summary__step-name">{s.name}</span>
              <span className="eod-summary__step-pts">{s.na ? 'Không có lệnh' : `${s.earned.toFixed(1)}/${s.max}đ`}</span>
            </div>
            <div className="eod-summary__track"><div style={{ width: `${s.na ? 0 : Math.min(100, s.pct)}%` }} className={s.pct >= 80 ? 'good' : s.pct >= 50 ? 'mid' : 'bad'} /></div>
            {!s.na && s.pct < 100 && onGoToStep && (
              <button type="button" className="eod-summary__go" onClick={() => onGoToStep(s.step)}>Điền ngay →</button>
            )}
          </div>
        ))}
        <div className="eod-summary__step">
          <div className="eod-summary__step-row">
            <span className="eod-summary__step-name">Cuối ngày</span>
            <span className={`eod-summary__step-pts ${reflectionDone ? 'is-profit' : ''}`}>{reflectionDone ? 'Đã viết' : 'Chưa viết'}</span>
          </div>
          <div className="eod-summary__track"><div style={{ width: reflectionDone ? '100%' : '0%' }} className="good" /></div>
          {!reflectionDone && (
            <button type="button" className="eod-summary__go" onClick={onGoToReflection}>Viết bài học →</button>
          )}
        </div>
      </div>

      <div className="eod-summary__bottom">
        <div className="eod-summary__box">
          <div className="eod-summary__box-title">Cần cải thiện</div>
          {issues.length === 0 ? (
            <div className="eod-summary__empty">✓ Không có lỗi nào được ghi nhận hôm nay.</div>
          ) : issues.map((i, idx) => (
            <button key={i.key} type="button" className="eod-summary__issue" onClick={onGoToOrders}>
              <span className="eod-summary__issue-num">{idx + 1}</span>
              <span className="eod-summary__issue-label">{i.label}</span>
              <span className="eod-summary__issue-count">{i.data?.count} lệnh</span>
            </button>
          ))}
        </div>
        <div className="eod-summary__box">
          <div className="eod-summary__box-title">Cam kết lần trước{prevCommitment ? ` · ${dayjs(prevCommitment.date).format('DD/MM')}` : ''}</div>
          {prevCommitment?.text ? (
            <blockquote className="eod-summary__quote">“{prevCommitment.text}”</blockquote>
          ) : (
            <div className="eod-summary__empty">Chưa có cam kết từ ngày trước.</div>
          )}
          {prevCommitment?.text && <div className="eod-summary__hint">Đối chiếu: hôm nay bạn đã giữ được cam kết này chưa?</div>}
        </div>
      </div>
    </div>
  );
};

export default EodSummaryCard;
